// Mirrors IdentifierFieldUnit in the API (Belteq.bMark.Model/Enums/Enums.cs).
export enum IdentifierFieldUnit {
    Field = 1,
    Metric = 2,
}

// What Dataset/Inspect read out of the workbook's Introduction tab. Any of them may be null, meaning
// the workbook did not say and the user picks for themselves.
export interface IDatasetFileMetadata {
    identifierFieldType: number | null
    identifierFieldUnit: IdentifierFieldUnit | null
    description: string | null
}

// Mirrors the measure properties of UserProductionRowModel. Also used at runtime to resolve tag
// attribute keys onto row properties, so it must stay a value rather than becoming a type.
export const PRODUCTION_MEASURE_KEYS = [
    'productionMonths',
    'rateTotalOil',
    'cumulativeTotalOil',
    'waterRate',
    'waterCumulative',
    'liquidRate',
    'liquideCumulative',
    'totalGasRate',
    'totalGasCumulative',
    'gasInjectionRate',
    'waterInjectionRate',
    'wells',
    'producingGasOilRatio',
    'producingGasLiquidRatio',
    'producingWaterOilRatio',
    'producingOilGasRatio',
    'producingWaterCut',
    'offtakeRateOilPerEur',
    'offtakeRateLiquidPerEur',
    'offtakeRateGasPerEur',
    'offtakeRateOilPerCumulative',
    'offtakeRateGasPerCumulative',
    'eurProducedOil',
    'eurProducedGas',
    'recoveryFactorOilProduction',
    'recoveryFactorFreeGas',
] as const;

export type ProductionMeasureKey = typeof PRODUCTION_MEASURE_KEYS[number];

// Rows are sparse and the API serialises with DefaultIgnoreCondition = WhenWritingNull, so absent
// measurements are missing keys rather than nulls. Optional throughout is deliberate.
export type IUserProductionRow = { date?: string } & Partial<Record<ProductionMeasureKey, number>>;

// fieldName and country are read from the first stored row, so both are absent for an empty
// series. Take the field name from the summary or local dataset state instead.
export interface IUserProductionSeries {
    datasetId: string;
    identifierFieldId: string;
    fieldName?: string;
    country?: string;
    unit: IdentifierFieldUnit;
    rows: IUserProductionRow[];
}

// The whole row comes back from an edit, not the cell that was written: an integer column rounds
// what was typed, and the stored value is derived through a unit conversion. Reconcile against this
// rather than keeping the typed value.
export interface IUserProductionRowResult {
    datasetId: string;
    identifierFieldId: string;
    unit: IdentifierFieldUnit;
    row: IUserProductionRow;
}

export interface IUserProductionFieldSummary {
    identifierFieldId: string;
    fieldName?: string;
    rowCount: number;
    firstDate?: string;
    lastDate?: string;
}

// Fields with no production data are absent from the array, not present with rowCount 0.
export interface IUserProductionSummary {
    datasetId: string;
    unit: IdentifierFieldUnit;
    fields: IUserProductionFieldSummary[];
}

export interface IProductionStageField {
    identifierFieldId: string;
    fieldName: string;
    newRows: number;
    overwrittenRows: number;
}

export enum ProductionProblemType {
    MissingFieldName = 0,
    MissingDate = 1,
    UnreadableValue = 2,
    OutOfRange = 3,
    DuplicateRow = 4,
    NotAWholeNumber = 5,
}

export interface IProductionProblemExample {
    lineNumber: number;
    value?: string;
}

// Examples is capped server-side at 20, so count and examples.length differ on a widely broken column.
export interface IProductionProblem {
  type: ProductionProblemType
  column: string
  count: number
  summary: string
  examples: IProductionProblemExample[]
}

// name is the string confirm expects back in createFields to create the field.
export interface IProductionStageNewField {
    name: string;
    country?: string;
    rows: number;
}

export interface IProductionStage {
  // stageId is absent when the upload is blocked — nothing is stored, so there is nothing to confirm
  stageId?: string
  datasetId: string
  uploadedUnit: IdentifierFieldUnit
  expiresUtc: string
  canConfirm: boolean
  rowsRead: number
  // rowsStaged counts only rows going to fields that already exist; rowsForNewFields covers the rest
  rowsStaged: number
  rowsForNewFields: number
  rowsIgnored: number
  fields: IProductionStageField[]
  newFieldCandidates: IProductionStageNewField[]
  ambiguousFieldNames: string[]
  problems: IProductionProblem[]
  blockedBy: string[]
}

export interface IProductionCreatedField {
    identifierFieldId: string;
    name: string;
    country?: string;
}

export interface IProductionConfirmResult {
    datasetId: string;
    rowsImported: number;
    rowsOverwritten: number;
    fieldsAffected: number;
    fieldsCreated: IProductionCreatedField[];
    // Rows held for a candidate the confirm did not ask to create. Dropped, not stored.
    rowsSkipped: number;
}

export interface IProductionDeleteResult {
    datasetId: string;
    identifierFieldId: string;
    fieldName?: string;
    rowsDeleted: number;
}
